import { useState } from 'react';
import { shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';

import { Flex } from '@zillow/constellation';
import { Label, Paragraph, Text, TextButton } from '@zillow/constellation-10';

import { useSelector } from '../../../../selectors/useSelector';

import { RECS, REVIEW_PAGE_SECTION_RECS } from '../../../../constants/RecommendationsConstants';

import Add3DToursCSEvents from '../../../../model/ClickstreamEvent/Add3DToursCSEvents';

import RecommendationsAlert from '../../../RecommendationsAlert';
import RecommendationsLearnMoreContent from '../../../RecommendationsLearnMoreContent';
import RecommendationIconBulb from '../../../RecommendationIconBulb';
import ListingTourFormField from '../../ListingTour/components/ListingTourFormField';
import PhotoUploadFormField from '../../AddPhotos/components/PhotoUploadFormField';
import VideoFormField from '../../../../components/WizardModularListing/Video';

import { StyledIconPlaceholder, StyledReviewSection, StyledReviewTitleRow } from '../StyledComponents';
import { ReviewSectionTitle } from '../../../../styles/wizard';

const { MEDIA } = REVIEW_PAGE_SECTION_RECS;

const propTypes = {
  onRecsClick: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
};

const defaultProps = {
  onRecsClick: null,
};

const MediaContent = ({ onRecsClick }) => {
  const { currentProperty, isMultifamily, recommendations, user } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      isMultifamily: state.properties.currentProperty.isMultifamily,
      recommendations: state.properties.recommendations,
      user: state.user,
    }),
    shallowEqual,
  );

  const [displayRecsModal, setDisplayRecsModal] = useState(false);

  return (
    <div data-testid="MediaContent" data-group-title="Media">
      {isMultifamily && <RecommendationsAlert sectionName={REVIEW_PAGE_SECTION_RECS.MEDIA} onRecsClick={onRecsClick} />}
      <StyledReviewSection data-testid="AddPhotos">
        <Flex display="flex" alignItems="center" marginBottom="sm">
          {!isMultifamily && (
            <StyledIconPlaceholder>
              {recommendations[MEDIA][RECS.PHOTOS] && (
                <RecommendationIconBulb
                  removeFlex={true}
                  onClick={() => {
                    setDisplayRecsModal(RECS.PHOTOS);
                  }}
                />
              )}
            </StyledIconPlaceholder>
          )}
          <ReviewSectionTitle>
            <Label required={isMultifamily} size="lg">
              {isMultifamily ? 'Building photos' : 'Photos'}
            </Label>
            {!isMultifamily && (
              <TextButton textStyle="body" onClick={() => setDisplayRecsModal(RECS.PHOTOS)}>
                Learn more
              </TextButton>
            )}
          </ReviewSectionTitle>
        </Flex>

        <StyledReviewTitleRow>
          {!isMultifamily && <StyledIconPlaceholder />}
          <PhotoUploadFormField />
        </StyledReviewTitleRow>
      </StyledReviewSection>

      <StyledReviewSection data-testid="AddListingTour">
        <Flex display="flex" alignItems="center" marginBottom="xs">
          {!isMultifamily && (
            <StyledIconPlaceholder>
              {recommendations[MEDIA][RECS.VIRTUAL_TOUR] && (
                <RecommendationIconBulb
                  removeFlex={true}
                  dataEventid={Add3DToursCSEvents.LearnMore.data_event_id}
                  onClick={() => {
                    Add3DToursCSEvents.LearnMore({ user, currentProperty });
                    setDisplayRecsModal(RECS.VIRTUAL_TOUR);
                  }}
                />
              )}
            </StyledIconPlaceholder>
          )}
          <ReviewSectionTitle>
            <Text textStyle="body-bold">{isMultifamily ? 'Amenity tours' : '3D Tour'}</Text>
            {!isMultifamily && (
              <TextButton
                textStyle="body"
                dataEventid={Add3DToursCSEvents.LearnMore.data_event_id}
                onClick={() => {
                  Add3DToursCSEvents.LearnMore({ user, currentProperty });
                  setDisplayRecsModal(RECS.VIRTUAL_TOUR);
                }}
              >
                Learn more
              </TextButton>
            )}
          </ReviewSectionTitle>
        </Flex>

        <Flex display="flex">
          {!isMultifamily && <StyledIconPlaceholder />}
          <ListingTourFormField />
        </Flex>
      </StyledReviewSection>

      {isMultifamily && (
        <StyledReviewSection data-testid="AddVideoLink">
          <Paragraph textStyle="body-bold" css={{ marginBottom: '400' }}>
            Video
          </Paragraph>
          <VideoFormField />
        </StyledReviewSection>
      )}

      {displayRecsModal && (
        <RecommendationsLearnMoreContent
          onClose={() => setDisplayRecsModal(false)}
          recommendationType={displayRecsModal}
        />
      )}
    </div>
  );
};

MediaContent.propTypes = propTypes;
MediaContent.defaultProps = defaultProps;

export default MediaContent;
